// frontend/src/pages/CreateInvoicePage.jsx
import React, { useEffect, useRef, useState } from "react";
import { apiGet, apiPost, apiPatch, apiErrorMessage, listify, getLastInvoiceForClient, getLastInvoiceForGroup, getLastIssuedInvoiceBefore, getDraftInvoicesForClient, apiGetAll, getInvoiceDescriptionPresets } from "../api";  // ✅ added apiPatch
import { useNavigate, useSearchParams } from "react-router-dom";
import { prefillFromInvoice, unchangedSinceLast, carriedHeader, carriedFromSource } from "./invoices/prefillFromInvoice";
import PreviousInvoicesPanel from "./invoices/PreviousInvoicesPanel";
import ExistingDraftChoice from "./invoices/ExistingDraftChoice";
import { applyPrefill } from "./invoices/applyPrefill";
import {
  presetsFor,
  fillPreset,
  yearEndWording,
  presetValueFor,
  presetListsFrom,
} from "./invoices/descriptionPresets";
import AddClientInline from "./invoices/AddClientInline";
import WipPanel from "./invoices/WipPanel";
import InvoiceDocument, { careOfLine } from "./invoices/InvoiceDocument";
import QboCustomerPanel from "./invoices/QboCustomerPanel";
import { canSyncToQbo, syncProblem, syncInvoiceToQbo, SYNC_LABEL } from "./invoices/qboSync";
import useInvoiceFontSize from "./invoices/useInvoiceFontSize";
import PageBreakChoice, {
  measurePrintLayout,
  wouldOverflow,
  pagesNeeded,
  fitScale,
  printSheetCss,
} from "./invoices/PageBreakChoice";
import { displayInvoiceNumber, invoicePdfTitle, isDraftNumber } from "../utils/invoiceNumber";

// The number immediately after `n`, zero-padded to match the backend's
// format_invoice_number. Used only to phrase the gap warning: when the real
// next number differs from this, some run in between is already taken.
const nextContiguous = (n) => {
  const s = String(n ?? "").trim();
  if (!/^\d+$/.test(s)) return null;
  return String(Number(s) + 1).padStart(4, "0");
};

// The zero-padded form format_invoice_number would store for `n` ("500" ->
// "0500"). Used to tell the user what a numeric override will actually be
// saved as, since the digits they type may not match that stored form.
const padNumber = (n) => {
  const s = String(n ?? "").trim();
  if (!/^\d+$/.test(s)) return null;
  return String(Number(s)).padStart(4, "0");
};

// The sticky preview pane: under the fixed 64px TopBar with a little air,
// and the same gap again at the foot of the screen. Below STACK_BELOW_PX the
// form and preview stack and the pane stops sticking.
const PREVIEW_TOP_PX = 76;
const PREVIEW_BOTTOM_GAP_PX = 12;
const STACK_BELOW_PX = 1000;

// A tick box and its words, reading as one control.
const tickLabel = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 13,
  cursor: "pointer",
  userSelect: "none",
};

const iconBtn = {
  width: 24,
  height: 24,
  lineHeight: "20px",
  padding: 0,
  borderRadius: 4,
  border: "1px solid #d1d5db",
  background: "white",
  cursor: "pointer",
  fontSize: 13,
};

const SMALL_LINK_BUTTON = {
  whiteSpace: "nowrap",
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  background: "white",
  cursor: "pointer",
  fontSize: 12,
};

export default function CreateInvoicePage({ user }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get("id"); // detect if editing
  const duplicateId = searchParams.get("duplicate"); // detect if duplicating

  const [clients, setClients] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [formClientQuery, setFormClientQuery] = useState("");
  const [formClientId, setFormClientId] = useState("");
  const [formShowSuggestions, setFormShowSuggestions] = useState(false);
  // Which suggestion the keyboard is on. The list was mouse-only, and because
  // the input sits inside the <form>, Enter SUBMITTED the invoice instead of
  // picking a client.
  const [highlightedClient, setHighlightedClient] = useState(0);
  // One element per visible suggestion, so the list can follow the highlight.
  const clientRowsRef = React.useRef([]);
  const [addingClient, setAddingClient] = useState(false);
  const [formSaved, setFormSaved] = useState(false);
  const [lastSavedInvoice, setLastSavedInvoice] = useState(null);
  const invoiceFontSize = useInvoiceFontSize();
  const [selectedClientDetail, setSelectedClientDetail] = useState(null); // full client record including address
  // The selected client's printed address, editable here. The invoice reads
  // street_address/city/province/postal_code off the client, and until now the
  // only screen that could write them was the add-a-client dialog -- the client
  // detail page edits a different, legacy `address` text field. So an address
  // that failed to import had nowhere to be entered.
  const [addressForm, setAddressForm] = useState({
    street_address: "", city: "", province: "", postal_code: "",
  });
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressSaved, setAddressSaved] = useState(false);
  const [addressError, setAddressError] = useState("");
  const [arBalance, setArBalance] = useState(null);       // open A/R for selected client
  const [arLoading, setArLoading] = useState(false);
  const [includePrevBalance, setIncludePrevBalance] = useState(false);
  const [numberClash, setNumberClash] = useState(null); // another invoice on this number
  const [numberUnlocked, setNumberUnlocked] = useState(false);
  const [numberHint, setNumberHint] = useState(null); // { nextAfter } from check_number
  const [autoNumber, setAutoNumber] = useState("");   // the previewed number, for revert
  // The invoice's QuickBooks state as the server last reported it. Kept apart
  // from `form`: none of it is edited here, and do_not_sync in the form may be
  // an unsaved tick, while the sync button must reflect what is saved.
  const [qbo, setQbo] = useState(null); // { qbo_id, sync_state, sync_error, do_not_sync }
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null); // { ok: bool, text }

  // What the banner reports, and what undo restores. `null` means no prefill
  // is in effect -- the banner and the undo button both key off it.
  // `carried` is the header snapshot (carriedHeader) the yellow marks compare
  // against; it is saved with the draft as prefill_carried and restored on
  // reopen, so the marks outlive the session. `copied` is a duplicate.
  const [prefill, setPrefill] = useState(null); // { number, date, clientName, carried, copied }
  const [prefillUndo, setPrefillUndo] = useState(null); // the form before it
  // The client's drafts, when one picked on a new invoice already has some:
  // { clientName, drafts }. Drives ExistingDraftChoice.
  const [existingDrafts, setExistingDrafts] = useState(null);

  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    due_date: "",
    number: "",
    discount: "",
    disbursements: "",
    subtotal: 0,
    tax_gst: 0,
    tax_pst: 0,
    total_amount: 0,
    status: "DRAFT",
    notes: "",
    lines: [{ description: "", amount: "" }],
    tax_mode_ui: "GST",
    year_end: new Date().getFullYear(),
    show_care_of: false,
    bill_to: "",       // invoice-level override for Bill To
    care_of: "",       // invoice-level c/o text (empty = not shown)
    intro_line: "Professional services to date including:",
    deposits_received: "",
    pst_exempt_amount: "",
    pst_exempt: false,
    gst_exempt: false,
    qbo_doc_number: "", // read-only QuickBooks doc number, set once pushed
    do_not_sync: false, // when true, ClientZone will not push this invoice to QuickBooks
    prefilled_from: null, // the invoice this one was prefilled/duplicated from
  });

  // Always mirrors `form`, so the async prefill can snapshot the form as it
  // stands without either closing over a stale value or reading state from
  // inside a setState updater.
  const formRef = useRef(form);
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  // The id of the most recently selected client, set synchronously in
  // selectClient. The async prefill checks this once its fetches resolve,
  // so a stale prefill (the user picked someone else in the meantime) is
  // dropped instead of landing on the wrong client's form.
  const selectedClientIdRef = useRef(null);
  // The invoice id loadInvoice last loaded, so a late comparison for one
  // invoice never lands on another.
  const loadedInvoiceIdRef = useRef(null);

  // The firm's standard descriptions, as edited in Settings. null until they
  // arrive -- and for good if the request fails -- which makes the picker use
  // the built-in lists, so writing an invoice never waits on this.
  const [presetLists, setPresetLists] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getInvoiceDescriptionPresets({ activeOnly: true })
      .then((rows) => {
        if (!cancelled) setPresetLists(presetListsFrom(rows));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Load clients + auto-number for new invoices
  useEffect(() => {
    (async () => {
      try {
        // apiGetAll, not apiGet: /api/clients/ is paginated, and listify() of
        // the first page left 14 of the firm's 1,014 clients unfindable in the
        // dropdown -- they simply were not in the list being searched.
        const c = await apiGetAll("/api/clients/");
        setClients(listify(c));
        // Auto-number only when creating fresh (not editing or duplicating)
        if (!editId && !duplicateId) {
          try {
            // Previewed for the hand-entry hints only: a new invoice is a
            // draft, and a draft takes no number until it is finalised.
            const nn = await nextNumberFor(formRef.current.date);
            setAutoNumber(nn.next_number || "");
          } catch (_) {}
        }
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, []);

  // ✅ If editing or duplicating, load existing invoice once after mount
  useEffect(() => {
    // See the call in loadInvoice. Lands on the form as it stands when the
    // fetches resolve -- lines are matched by description, so one already
    // edited simply matches nothing -- and only if this is still the invoice
    // on screen. Marked `derived`: the comparison is shown, never saved back
    // onto the draft as though it had been prefilled.
    const compareWithLastIssued = async (invoiceId, invoice) => {
      try {
        // The client's LAST issued invoice, even one issued after this draft
        // was started (owner, 2026-11-02).
        const source = await getLastIssuedInvoiceBefore(invoice.client, {
          excludeId: invoiceId,
        });
        if (!source || loadedInvoiceIdRef.current !== String(invoiceId)) return;
        setForm((prev) => ({ ...prev, lines: carriedFromSource(prev.lines, source).lines }));
        setPrefill({
          number: source.number,
          date: source.date,
          clientName: null,
          carried: carriedFromSource([], source).carried,
          derived: true,
        });
      } catch {
        // No comparison is the old behaviour, not an error worth showing.
      }
    };

    const loadInvoice = async (invoiceId, isDuplicate = false) => {
      loadedInvoiceIdRef.current = String(invoiceId);
      try {
        const invoice = await apiGet(`/api/invoices/${invoiceId}/`);

        // For duplicates, strip identifying fields
        const newInvoiceData = {
          // A duplicate is a NEW invoice, so it takes today. An edit is the
          // same invoice, and must keep the date it was issued on -- stamping
          // today over it moved a July invoice into October on the next save,
          // and with it into the wrong GST filing period.
          date: isDuplicate
            ? new Date().toISOString().slice(0, 10)
            : invoice.date || new Date().toISOString().slice(0, 10),
          due_date: invoice.due_date || "",
          number: invoice.number || "",
          discount: invoice.discount_value ? String(invoice.discount_value) : "",
          disbursements: invoice.disbursements ? String(invoice.disbursements) : "",
          subtotal: invoice.subtotal || 0,
          tax_gst: invoice.gst || 0,
          tax_pst: invoice.pst || 0,
          total_amount: invoice.total || 0,
          // A duplicate is a new draft. An edit keeps its status: resetting it
          // to DRAFT here meant "Update Invoice" on a Sent invoice quietly
          // reopened it as a draft.
          status: isDuplicate ? "DRAFT" : (invoice.status || "DRAFT"),
          notes: invoice.notes || "",
          lines: (invoice.lines || [{ description: "", amount: "" }]).map(l => ({
            ...l,
            amount: l.amount && parseFloat(l.amount) !== 0 ? l.amount : "",
          })),
          tax_mode_ui: invoice.tax_mode || "GST",
          year_end: invoice.year_end || new Date().getFullYear(),
          show_care_of: invoice.show_care_of || false,
          bill_to: invoice.bill_to || "",
          care_of: invoice.care_of || "",
          // ?? not ||, so an invoice deliberately saved with no intro line
          // does not silently regain the firm default on reopening.
          intro_line: invoice.intro_line ?? "Professional services to date including:",
          deposits_received: invoice.deposits_received || "",
          pst_exempt_amount: invoice.pst_exempt_amount || "",
          pst_exempt: Boolean(invoice.pst_exempt),
          gst_exempt: Boolean(invoice.gst_exempt),
          qbo_doc_number: invoice.qbo_doc_number || "",
          do_not_sync: invoice.do_not_sync || false,
          prefilled_from: invoice.prefilled_from ?? null,
        };

        if (isDuplicate) {
          // A duplicate is a prefill from the invoice the user chose: the same
          // whole-invoice reading (feesOf -- an imported single-line invoice
          // comes across at its full fee), the same yellow, and the same
          // record of where it came from.
          const patch = prefillFromInvoice(invoice, {
            nextNumber: "", today: newInvoiceData.date,
          });
          Object.assign(newInvoiceData, {
            lines: patch.lines.map((l) => ({
              ...l,
              _carried: { description: l.description, amount: l.amount },
            })),
            discount: patch.discount,
            disbursements: patch.disbursements,
            tax_mode_ui: patch.tax_mode_ui,
            pst_exempt: patch.pst_exempt,
            gst_exempt: patch.gst_exempt,
            prefilled_from: patch.prefilled_from,
          });
          setPrefill({
            number: invoice.number, date: invoice.date, clientName: null,
            carried: carriedHeader(patch), copied: true,
          });
        } else {
          // Reopening: each line's snapshot comes back from InvoiceLine.carried
          // and the header's from prefill_carried. Only a DRAFT is marked --
          // the server drops both once an invoice is issued, and the marks are
          // gated on status besides.
          newInvoiceData.lines = newInvoiceData.lines.map((l) => {
            const { carried, ...rest } = l;
            return carried ? { ...rest, _carried: carried } : rest;
          });
          const isDraft = (invoice.status || "DRAFT").toUpperCase() === "DRAFT";
          const anyCarried = Boolean(invoice.prefill_carried) ||
            newInvoiceData.lines.some((l) => l._carried);
          setPrefill(isDraft && anyCarried ? {
            number: invoice.prefilled_from_number || null,
            date: invoice.prefilled_from_date || null,
            clientName: null,
            carried: invoice.prefill_carried || {},
          } : null);
          // A draft with NO snapshot -- everything saved before 2026-10-26,
          // the drafts loaded from paper, anything typed by hand -- reopened
          // with no yellow at all, which is what the firm kept seeing. It is
          // compared instead with the client's last issued invoice dated on
          // or before it: the bill it would have been prefilled from.
          if (isDraft && !anyCarried && invoice.client) {
            compareWithLastIssued(invoiceId, invoice);
          }
        }
        setPrefillUndo(null);

        // For duplicates, fetch a fresh next number. It's a brand-new,
        // unsynced invoice, so it has no QBO doc number yet either.
        if (isDuplicate) {
          newInvoiceData.qbo_doc_number = "";
          try {
            const nn = await nextNumberFor(newInvoiceData.date);
            // The copy is a new draft: numbered when it is finalised.
            newInvoiceData.number = "";
            setAutoNumber(nn.next_number || "");
          } catch (_) {}
        }

        setForm(newInvoiceData);
        setQbo(isDuplicate ? null : {
          qbo_id: invoice.qbo_id || null,
          sync_state: invoice.sync_state || "not_synced",
          sync_error: invoice.sync_error || "",
          do_not_sync: Boolean(invoice.do_not_sync),
        });
        setFormClientId(invoice.client);
        setFormClientQuery(invoice.client_name || "");

        // Fetch full client detail for address fields
        apiGet(`/api/clients/${invoice.client}/`)
          .then((detail) => setSelectedClientDetail(detail))
          .catch(() => {});

        // Fetch open A/R for this client (exclude self if editing)
        const excludeParam = !isDuplicate ? `&exclude=${invoiceId}` : "";
        apiGet(`/api/invoices/ar_balance/?client=${invoice.client}${excludeParam}`)
          .then((res) => setArBalance(parseFloat(res.balance || 0)))
          .catch(() => {});
      } catch (e) {
        setErr("Failed to load invoice for editing: " + e.message);
      }
    };

    if (editId) loadInvoice(editId, false);
    else if (duplicateId) loadInvoice(duplicateId, true);
  }, [editId, duplicateId]);

  useEffect(() => {
    const clientId = sessionStorage.getItem("createInvoiceClientId");
    const clientName = sessionStorage.getItem("createInvoiceClientName");

    if (clientId && clientName) {
      setFormClientId(Number(clientId));
      setFormClientQuery(clientName);
      sessionStorage.removeItem("createInvoiceClientId");
      sessionStorage.removeItem("createInvoiceClientName");
      // Fetch full client data to pre-populate bill_to, care_of, and address
      (async () => {
        try {
          const clientData = await apiGet(`/api/clients/${clientId}/`);
          setSelectedClientDetail(clientData);
          setForm((prev) => ({
            ...prev,
            bill_to: clientData.bill_to || clientData.name || "",
            care_of: prev.care_of, // keep existing
          }));
        } catch (e) {
          // non-fatal
        }
      })();
    }
  }, []);

  // Totals calc
  useEffect(() => {
    const subtotal = form.lines.reduce(
      (sum, l) => sum + parseFloat(l.amount || 0),
      0
    );
    const discount = parseFloat(form.discount || "0");
    const disb = parseFloat(form.disbursements || "0");
    const base = subtotal - discount + disb;
    // The two exemptions are independent: one is federal, one provincial. A
    // client outside Saskatchewan bears GST on the whole base and no PST at
    // all. Mirrors _compute_numbers in invoicing/serializers.py, which is what
    // the saved figures actually come from.
    const charged = form.tax_mode_ui === "GST" || form.tax_mode_ui === "GST_PST";
    const gst = charged && !form.gst_exempt ? base * 0.05 : 0;
    // The amount is historical: the 2026 invoices loaded from paper recorded
    // their exempt portion that way. Nothing new writes it.
    const pstExemptAmount = Math.min(
      parseFloat(form.pst_exempt_amount || "0") || 0,
      Math.max(base, 0)
    );
    const pst =
      form.tax_mode_ui === "GST_PST" && !form.pst_exempt
        ? (base - pstExemptAmount) * 0.06
        : 0;
    const hst = form.tax_mode_ui === "HST" ? base * 0.13 : 0;
    const total = base + gst + pst + hst;
    setForm((prev) => ({
      ...prev,
      subtotal: subtotal.toFixed(2),
      tax_gst: gst.toFixed(2),
      tax_pst: pst.toFixed(2),
      tax_hst: hst.toFixed(2),
      total_amount: total.toFixed(2),
    }));
  }, [form.lines, form.discount, form.disbursements, form.tax_mode_ui,
      form.pst_exempt_amount, form.pst_exempt, form.gst_exempt]);

  // Warn when a hand-edited number is already on another invoice. The number is
  // unique in the database, so a clash cannot be saved — the fix is to edit it.
  useEffect(() => {
    const active = editId || numberUnlocked;
    if (!active || !form.number) {
      setNumberClash(null);
      setNumberHint(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const qs = new URLSearchParams({ number: form.number });
        if (editId) qs.set("exclude", editId);
        const res = await apiGet(`/api/invoices/check_number/?${qs}`);
        setNumberClash(res.duplicate ? res.invoice : null);
        setNumberHint({ nextAfter: res.next_after ?? null });
      } catch (_) {
        setNumberClash(null);
        setNumberHint(null);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [form.number, editId, numberUnlocked]);

  // DRF reports field errors as {field: ["message", …]}. Show the messages, not
  // the JSON envelope.
  const formatApiError = (e) => {
    const data = e?.response?.data;
    if (data && typeof data === "object") {
      const messages = Object.values(data)
        .flat()
        .filter((m) => typeof m === "string");
      if (messages.length) return messages.join(" ");
    }
    return e?.message || "Save failed.";
  };

  // Grow a description box to fit its text. Wrapping alone is not enough — a
  // fixed-height textarea just hides the overflow behind a scrollbar.
  // Invoice numbers run per FISCAL YEAR ("27-001"), and the run is chosen by
  // the invoice's own date -- not today's. The firm enters a backlog months
  // after the fact, so previewing against today would show the wrong year.
  const nextNumberFor = (date) =>
    apiGet(
      date
        ? `/api/invoices/next_number/?date=${encodeURIComponent(date)}`
        : "/api/invoices/next_number/"
    );

  // One list, used by the dropdown AND the keyboard, so they cannot disagree
  // about which client Enter picks.
  const matchingClients = React.useMemo(() => {
    const query = (formClientQuery || "").toLowerCase();
    if (!query) return [];
    return clients.filter(
      (c) => c.is_active && (c.name || "").toLowerCase().includes(query)
    );
  }, [clients, formClientQuery]);

  // The suggestion list is 220px tall and scrolls, so a highlight moved by the
  // arrow keys can land below the fold. Without this it read as "the down
  // arrow does nothing": the highlight WAS moving, just out of sight.
  //
  // "nearest" scrolls the least it can, so walking down one row at a time
  // nudges the list instead of jumping it.
  React.useEffect(() => {
    if (!formShowSuggestions) return;
    const row = clientRowsRef.current[highlightedClient];
    if (row && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest" });
    }
  }, [highlightedClient, formShowSuggestions, matchingClients]);

  // Shared by the preset picker: the same values must fill a preset and
  // recognise it afterwards, or the picker would not match its own output.
  const presetContext = {
    year: form.year_end,
    yearEnd: yearEndWording(selectedClientDetail, form.year_end),
  };

  const addressOf = (client) => ({
    street_address: (client && client.street_address) || "",
    city: (client && client.city) || "",
    province: (client && client.province) || "",
    postal_code: (client && client.postal_code) || "",
  });

  const addressIsEmpty = (a) =>
    !a.street_address && !a.city && !a.province && !a.postal_code;

  // The boxes follow the client record, however it arrived: picked from the
  // search, loaded with a draft being edited or duplicated, or handed over
  // from the client page. Filling them only on a search pick left a reopened
  // draft showing BLANK boxes and "None on file" for a client with an address,
  // so a saved address looked unsaved -- and re-saving a half-retyped blank
  // form would PATCH "" over the fields left empty. Keyed on the stored values,
  // not the object, so typing is never overwritten mid-edit.
  const detailAddress = addressOf(selectedClientDetail);
  useEffect(() => {
    setAddressSaved(false);
    setAddressError("");
  }, [selectedClientDetail?.id]);
  useEffect(() => {
    // Not the "Saved" tick: a save lands here too, with the values just saved.
    setAddressForm(detailAddress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedClientDetail?.id,
    detailAddress.street_address,
    detailAddress.city,
    detailAddress.province,
    detailAddress.postal_code,
  ]);

  /** Write the address back to the CLIENT, so it reaches every invoice. */
  const saveAddress = async () => {
    const id = selectedClientDetail?.id || formClientId;
    if (!id) return;
    setAddressSaving(true);
    setAddressError("");
    try {
      const updated = await apiPatch(`/api/clients/${id}/`, addressForm);
      // Trust the server's echo where it gives one, so the preview shows what
      // was actually stored rather than what was typed.
      setSelectedClientDetail((prev) => ({ ...(prev || {}), ...(updated || addressForm) }));
      setAddressSaved(true);
    } catch (e) {
      setAddressError(formatApiError(e) || "Could not save the address.");
    } finally {
      setAddressSaving(false);
    }
  };

  // Changing the date can move the invoice into a different fiscal run, so the
  // previewed number has to follow it. Only for a fresh invoice whose number
  // the operator has not taken over by hand -- an edit keeps its own number,
  // and an unlocked one is theirs to set.
  useEffect(() => {
    if (editId || duplicateId || numberUnlocked || !form.date) return;
    let cancelled = false;
    nextNumberFor(form.date)
      .then((nn) => {
        if (cancelled || !nn?.next_number) return;
        setAutoNumber(nn.next_number);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.date, editId, duplicateId, numberUnlocked]);

  const autoGrow = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const updateLine = (idx, field, value) => {
    // Replace the line, never assign into it. [...form.lines] is a SHALLOW
    // copy, so the old code mutated the very object every snapshot of the form
    // was holding -- the prefill's "has the user touched this?" check and the
    // Undo snapshot were both reading state that typing had rewritten
    // underneath them. That is what made an invoice's date revert to today.
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, i) =>
        i === idx ? { ...line, [field]: value } : line
      ),
    }));
    setFormSaved(false);
  };

  const addLine = () => {
    setForm({
      ...form,
      lines: [...form.lines, { description: "", amount: "" }],
    });
    setFormSaved(false);
  };

  const removeLine = (idx) => {
    setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));
    setFormSaved(false);
  };

  /** Put a fresh line ABOVE this one. Work does not always get remembered in
   *  the order it is billed, and appending to the bottom then meant retyping
   *  everything below it. */
  const insertLineAbove = (idx) => {
    setForm((prev) => {
      const lines = [...prev.lines];
      lines.splice(idx, 0, { description: "", amount: "" });
      return { ...prev, lines };
    });
    setFormSaved(false);
  };

  /** Move line `from` so it sits before what is now line `to` (a gap index,
   *  0..lines.length), carrying its fee with it. Lines are reordered by
   *  dragging a grip, QuickBooks-style, or Alt+arrow on the grip. */
  const moveLineTo = (from, to) => {
    setForm((prev) => {
      if (from < 0 || from >= prev.lines.length) return prev;
      const gap = Math.max(0, Math.min(to, prev.lines.length));
      // Dropping into the gap just above or below itself is a no-op.
      if (gap === from || gap === from + 1) return prev;
      const lines = [...prev.lines];
      const [moved] = lines.splice(from, 1);
      lines.splice(gap > from ? gap - 1 : gap, 0, moved);
      return { ...prev, lines };
    });
    setFormSaved(false);
  };

  // While a line is being dragged: which line, and the gap it would drop into.
  const [dragLine, setDragLine] = useState(null);
  const [dropGap, setDropGap] = useState(null);

  // The gap a pointer over line `idx` means: above it on its top half, below
  // it on its bottom half.
  const gapAt = (e, idx) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return e.clientY < rect.top + rect.height / 2 ? idx : idx + 1;
  };

  const endLineDrag = () => {
    setDragLine(null);
    setDropGap(null);
  };

  function buildPayload() {
    const payload = {
      client: formClientId || null,
      date: form.date || null,
      due_date: form.due_date || null,
      tax_mode: form.tax_mode_ui,
      discount_type: form.discount ? "AMOUNT" : "NONE",
      discount_value: parseFloat(form.discount || "0"),
      disbursements: parseFloat(form.disbursements || "0"),
      status: form.status || "DRAFT",
      notes: form.notes || "",
      year_end: form.year_end || null,
      show_care_of: form.show_care_of || false,
      bill_to: form.bill_to || "",
      care_of: form.care_of || "",
      // Sent even when empty: blank is a real choice, not "use the default".
      intro_line: form.intro_line ?? "",
      deposits_received: parseFloat(form.deposits_received || "0"),
      pst_exempt_amount: parseFloat(form.pst_exempt_amount || "0"),
      pst_exempt: Boolean(form.pst_exempt),
      gst_exempt: Boolean(form.gst_exempt),
      // Stored, not left to be re-derived later: a historical invoice must
      // print the balance the client actually saw on the day it was issued.
      previous_balance:
        includePrevBalance && arBalance > 0 ? arBalance : 0,
      do_not_sync: form.do_not_sync || false,
      // Where the prefill came from and what it carried, so a reopened draft
      // can still show which figures nobody has revisited. The server keeps
      // these on drafts only.
      // A `derived` comparison (a draft that had no snapshot) is not one: it
      // is recomputed on every open and never stored as a prefill.
      prefilled_from: form.prefilled_from ?? null,
      prefill_carried: prefill && !prefill.derived ? prefill.carried || null : null,
      lines: form.lines.map((l) => ({
        description: l.description,
        amount: parseFloat(l.amount || "0"),
        carried: prefill && !prefill.derived && l._carried ? l._carried : null,
      })),
    };

    // An edit always carries its number; a create carries one only when the
    // user overrode it, so an untouched create still gets a server-allocated
    // number.
    if (editId || numberUnlocked) {
      payload.number = form.number;
    }
    return payload;
  }

  // Issue: save the draft as FINAL, which gives it its real number so it can
  // be printed and sent. It does NOT go to QuickBooks -- that is "Sync to
  // QuickBooks", once the invoice is marked Sent. Stays on this screen so the
  // next thing to do, printing, is right here.
  async function issueInvoice() {
    if (!window.confirm(
      "This gives the invoice its number. It will NOT be sent to QuickBooks until you sync it."
    )) return;
    setBusy(true);
    setErr("");
    try {
      const payload = { ...buildPayload(), status: "FINAL" };
      const saved = editId
        ? await apiPatch(`/api/invoices/${editId}/`, payload)
        : await apiPost("/api/invoices/", payload);
      setForm((f) => ({
        ...f,
        status: saved?.status || "FINAL",
        number: saved?.number || f.number,
      }));
      setFormSaved(false);
      if (!editId && saved?.id) {
        // Now an existing invoice: carry on editing (and printing) it.
        navigate(`/invoices/create?id=${saved.id}`, { replace: true });
      }
    } catch (e) {
      setErr(apiErrorMessage(e, "Could not issue the invoice."));
    } finally {
      setBusy(false);
    }
  }

  // Push the invoice as last SAVED to QuickBooks. Unsaved edits are not sent.
  async function syncToQbo() {
    setSyncBusy(true);
    setSyncMessage(null);
    try {
      const saved = await syncInvoiceToQbo(editId);
      const next = {
        qbo_id: saved?.qbo_id || null,
        sync_state: saved?.sync_state || "not_synced",
        sync_error: saved?.sync_error || "",
        do_not_sync: Boolean(saved?.do_not_sync),
      };
      setQbo(next);
      const problem = syncProblem(next);
      setSyncMessage(problem ? { ok: false, text: problem } : { ok: true, text: "Synced to QuickBooks." });
    } catch (e) {
      // Refused before anything was sent: the invoice keeps its number and status.
      setSyncMessage({ ok: false, text: apiErrorMessage(e, "Could not sync to QuickBooks.") });
    } finally {
      setSyncBusy(false);
    }
  }

  async function saveInvoice(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const payload = buildPayload();

      if (editId) {
        await apiPatch(`/api/invoices/${editId}/`, payload);
      } else {
        await apiPost("/api/invoices/", payload);
      }

      setFormSaved(true);
      setLastSavedInvoice({ ...form, clientId: formClientId });

      // Straight to the list, where the new invoice is at the top -- so saving
      // visibly does something. Only on success: a failed save must leave the
      // form exactly where it is, with the error on screen.
      navigate("/invoices");

      if (!editId) {
        setForm({
          date: new Date().toISOString().slice(0, 10),
          due_date: "",
          number: "",
          discount: "",
          disbursements: "",
          subtotal: 0,
          tax_gst: 0,
          tax_pst: 0,
          total_amount: 0,
          status: "DRAFT",
          notes: "",
          lines: [{ description: "", amount: "" }],
          tax_mode_ui: "GST",
          year_end: new Date().getFullYear(),
          show_care_of: false,
          bill_to: "",
          care_of: "",
          qbo_doc_number: "",
          do_not_sync: false,
          prefilled_from: null,
        });
        setFormClientQuery("");
        setFormClientId("");
        // The ref is the form's identity for every in-flight fetch. Left
        // pointing at the client just saved, a prefill still in flight would
        // pass its guard and repopulate the blank form we just handed back.
        selectedClientIdRef.current = null;
        setSelectedClientDetail(null);
        setArBalance(null);
        setIncludePrevBalance(false);
        // The banner describes a form that no longer exists, and its Undo
        // would restore the pre-prefill state of the invoice already saved.
        setPrefill(null);
        setPrefillUndo(null);

        // Show the number the next invoice will be given.
        setNumberUnlocked(false);
        setNumberClash(null);
        setNumberHint(null);
        try {
          const nn = await nextNumberFor(formRef.current.date);
          setForm((prev) => ({ ...prev, number: "" }));
          setAutoNumber(nn.next_number || "");
        } catch (_) {}
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  const field = {
    padding: 8,
    borderRadius: 6,
    border: "1px solid #e5e7eb",
    width: "100%",
  };
  // A fee, discount or disbursement still exactly as the last invoice had it
  // shows yellow until someone changes it: last year's figure going out again
  // should be a decision, not an oversight.
  //
  // Only on a DRAFT: once issued, the figures are the invoice's own (the
  // server drops the snapshots then too). Michael, 2026-10-26: a carried field
  // stays yellow "until its value is changed, or until the invoice is no
  // longer a draft".
  const isDraftForm = (form.status || "DRAFT").toUpperCase() === "DRAFT";
  const carriedMark = (value, carried) =>
    prefill && isDraftForm && unchangedSinceLast(value, carried)
      ? {
          "data-unchanged": "true",
          title: "Unchanged from the last invoice",
          style: { ...field, background: "#fef9c3", borderColor: "#facc15" },
        }
      : { style: field };
  // A tick carried from the last invoice, still ticked. An unticked box
  // carried nothing worth flagging, as a blank amount does not.
  const carriedTick = (checked, carried) =>
    prefill && isDraftForm && carried === true && checked === true
      ? {
          "data-unchanged": "true",
          title: "Unchanged from the last invoice",
          style: { background: "#fef9c3", borderRadius: 4, padding: "0 4px" },
        }
      : {};
  // Money is typed, never nudged: no spinner arrows, and neither the arrow
  // keys nor the scroll wheel over a focused field change the figure.
  const moneyInput = {
    type: "number",
    step: "0.01",
    className: "no-spin",
    onKeyDown: (e) => {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
    },
    onWheel: (e) => e.currentTarget.blur(),
  };
  const primaryBtn = {
    padding: "10px 14px",
    borderRadius: 6,
    border: 0,
    background: "#111827",
    color: "white",
    fontWeight: 700,
    cursor: "pointer",
    marginTop: 8,
  };
  const secondaryBtn = {
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#f9fafb",
    cursor: "pointer",
  };
  const linkBtn = {
    background: "transparent",
    border: "none",
    color: "#2563eb",
    cursor: "pointer",
    padding: 0,
  };

  // Determine if current user is a manager (can edit locked invoices)
  const isManager = user && (
    user.is_staff ||
    user.is_superuser ||
    ["ADMIN", "MANAGER"].includes((user.role || "").toUpperCase()) ||
    ["ADMIN", "MANAGER"].includes((user.employee_role || "").toUpperCase())
  );

  // Invoice lock state — non-draft invoices are locked for non-managers
  const invoiceStatus = (editId ? (form.status || "DRAFT") : "DRAFT").toUpperCase();
  const isLocked = ["FINAL", "SENT", "PAID"].includes(invoiceStatus) && !isManager;
  // Issuing and syncing follow the per-person flag, as on the Invoices list.
  const canFinalize = Boolean(user?.can_finalize_invoices || user?.is_superuser);
  const showIssue = canFinalize && invoiceStatus === "DRAFT" && !isLocked;
  const showSync = canFinalize && Boolean(editId) && Boolean(qbo) &&
    canSyncToQbo({ ...qbo, status: invoiceStatus });
  const savedSyncProblem = qbo && !syncMessage ? syncProblem(qbo) : null;

  const selectedClient = clients.find((c) => c.id === formClientId);
  const clientForPreview = (() => {
    // Use selectedClientDetail (full record with address) when available,
    // otherwise fall back to the list-serializer client object
    const id = formSaved && lastSavedInvoice ? lastSavedInvoice.clientId : formClientId;
    if (selectedClientDetail && selectedClientDetail.id === id) {
      return selectedClientDetail;
    }
    return clients.find((c) => c.id === id) || null;
  })();
  const previewData = formSaved && lastSavedInvoice ? lastSavedInvoice : form;

  // Print to PDF function
  // Asked only when the invoice will not fit one sheet: { pages, scale }.
  const [printChoice, setPrintChoice] = useState(null);

  const handlePrintToPDF = () => {
    const layout = measurePrintLayout(document.getElementById("invoice-preview"));
    if (wouldOverflow(layout)) {
      setPrintChoice({ pages: pagesNeeded(layout), scale: fitScale(layout) });
      return;
    }
    openPrintWindow("fit", fitScale(layout));
  };

  const choosePageMode = (mode) => {
    const scale = printChoice?.scale || 1;
    setPrintChoice(null);
    openPrintWindow(mode, mode === "fit" ? scale : 1);
  };

  const openPrintWindow = (mode, scale) => {
    // Clone the invoice preview node
    const invoiceNode = document
      .getElementById("invoice-preview")
      .cloneNode(true);

    // Remove elements you don’t want in print
    const sash = invoiceNode.querySelector("#preview-sash");
    if (sash) sash.remove();
    // Every control, not just the print button: "Edit Invoice" printed on the
    // client's copy of 27-027.
    invoiceNode
      .querySelectorAll("#print-button, #edit-button, .screen-only")
      .forEach((node) => node.remove());

    // Grab the cleaned HTML
    const invoiceContent = invoiceNode.innerHTML;

    // Open print window
    const printWindow = window.open("", "_blank", "width=800,height=1000");
    printWindow.document.open();
    printWindow.document.write(`
    <html>
      <head>
        <title>Invoice</title>
        <style>
          body {
            font-family: 'Times New Roman', Times, serif;
            color: #000;
            background: white;
            margin: 0;
            font-size: 13px;
            line-height: 1.5;
          }
          /* The same sheet the Invoices page prints: letter, 0.5in margins,
             one sheet tall -- or zoomed onto it, or running on. */
          ${printSheetCss("#invoice-preview", { mode, scale })}
          /* Ensure no sash/print button sneak in */
          #preview-sash, #print-button, #edit-button, .screen-only {
            display: none !important;
          }
        </style>
      </head>
      <body>
        <div id="invoice-preview">
          ${invoiceContent}
        </div>
        <script>
          window.onload = () => {
            window.print();
            window.onafterprint = () => window.close();
          };
        </script>
      </body>
    </html>
  `);
    printWindow.document.close();
    // Set after writing, not in <title>: a client name like "Smith & Co"
    // would otherwise need escaping. Save-as-PDF names the file from it.
    printWindow.document.title = invoicePdfTitle({
      clientName: clientForPreview?.name,
      number: previewData.number,
      date: previewData.date,
    });
  };

  const selectClient = (c) => {
    // Set synchronously (not via state) so the async prefill below can tell,
    // once its awaits resolve, whether `c` is still the selected client --
    // state updates are batched/async and would not be readable in time.
    selectedClientIdRef.current = c.id;
    setFormClientId(c.id);
    setFormClientQuery(c.name);
    setFormShowSuggestions(false);
    setFormSaved(false);
    setForm((prev) => ({
      ...prev,
      bill_to: c.bill_to || c.name || "",
      care_of: "",
    }));
    setIncludePrevBalance(false);
    // The form as it stands at THIS moment. The prefill below lands several
    // network round trips later, and anything the user typed in between is
    // theirs -- see applyPrefill().
    const formAtSelection = formRef.current;
    // Every one of these lands asynchronously, so every one of them checks
    // the same ref the prefill does. A/R gates the "Include previous balance"
    // checkbox, which PRINTS a "Previous Balance" and a "Balance Due" row on
    // the invoice the client receives; the client detail supplies the printed
    // address and the group the WIP panel rolls up. A response arriving under
    // a client it does not belong to is the same defect in each case.
    const isStillSelected = () => selectedClientIdRef.current === c.id;

    // Fetch full client detail (has address fields not in list serializer)
    apiGet(`/api/clients/${c.id}/`)
      .then((detail) => {
        if (!isStillSelected()) return;
        setSelectedClientDetail(detail);
      })
      .catch(() => { if (isStillSelected()) setSelectedClientDetail(c); });
    // Fetch open A/R balance for this client
    setArLoading(true);
    const excludeParam = editId ? `&exclude=${editId}` : "";
    apiGet(`/api/invoices/ar_balance/?client=${c.id}${excludeParam}`)
      .then((res) => { if (isStillSelected()) setArBalance(parseFloat(res.balance || 0)); })
      .catch(() => { if (isStillSelected()) setArBalance(null); })
      // A stale fetch must not clear the newer selection's loading state
      // either -- that flag belongs to whoever is selected now.
      .finally(() => { if (isStillSelected()) setArLoading(false); });

    // A second draft for one client is how the same work gets billed twice,
    // so a NEW invoice asks first when the client already has a draft. The
    // prefill below carries on meanwhile; "Create another draft" just closes.
    setExistingDrafts(null);
    if (!editId) {
      getDraftInvoicesForClient(c.id)
        .then((drafts) => {
          if (isStillSelected() && drafts.length) {
            setExistingDrafts({ clientName: c.name, drafts });
          }
        })
        .catch(() => {});
    }

    // Only a brand-new invoice prefills. Editing has real content to protect,
    // and duplicating already copied an invoice the user chose deliberately.
    if (editId || duplicateId) return;
    setPrefill(null);
    setPrefillUndo(null);
    (async () => {
      try {
        let source = await getLastInvoiceForClient(c.id);
        // `group` is on the client list payload (ClientSerializer exposes
        // `group` and `group_name`), so no extra fetch is needed to know
        // whether a fallback is even possible.
        if (!source && c.group) {
          source = await getLastInvoiceForGroup(c.group, c.id);
        }
        if (!source) return;
        // A prefilled invoice is a new draft: no number until finalised.
        const nextNumber = "";
        const patch = prefillFromInvoice(source, {
          nextNumber,
          today: new Date().toISOString().slice(0, 10),
        });
        // Each line remembers what it was carried as, so it can show yellow
        // until changed -- on the line itself, so the mark follows the line
        // when it is dragged. Saved with the line (InvoiceLine.carried) so
        // the mark is still there when the draft is reopened.
        patch.lines = patch.lines.map((l) => ({
          ...l,
          _carried: { description: l.description, amount: l.amount },
        }));
        // The user may have picked a different client while these awaits
        // were in flight. Landing this prefill now would overwrite whatever
        // that later selection put on the form with the wrong client's
        // lines, amounts and notes -- under a banner that names neither. If
        // `c` is no longer the selected client, drop the result entirely;
        // a partial apply (e.g. still showing the banner) would be worse.
        if (selectedClientIdRef.current !== c.id) return;
        // The undo snapshot is taken from a ref, NOT from inside a setForm
        // updater: updaters must be pure, and React may invoke one twice.
        setPrefillUndo(formRef.current);
        setForm((prev) => applyPrefill(prev, patch, formAtSelection));
        setPrefill({
          number: source.number,
          date: source.date,
          clientName: source._sourceClientName || null,
          carried: carriedHeader(patch),
        });
      } catch {
        // A failed prefill is a convenience that did not happen, not an error
        // worth blocking the invoice over. The form stays exactly as it was.
      }
    })();
  };

  return (
    <>
      <style>
        {`
    /* Cmd+P on this page prints the preview alone, on the same sheet as the
       Print button. */
    ${printSheetCss("#invoice-preview")}
    .no-spin::-webkit-outer-spin-button,
    .no-spin::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    .no-spin { -moz-appearance: textfield; appearance: textfield; }
    @media print {
      #preview-sash, #print-button, #edit-button {
        display: none !important;
      }
    }
    /* Form and preview side by side. minmax(0, 1fr), not 1fr, so a long
       unbroken line in either column cannot push the page wider. */
    .create-invoice-grid {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 24px;
    }
    /* "Need to scroll on the invoice so it can be seen while drafting"
       (2026-10-30): the preview stays in view under the fixed 64px top bar
       while the form scrolls, and scrolls itself when taller than the
       screen. align-self: start, or the grid stretches the pane to the
       form's height and there is nothing for it to stick within. */
    .invoice-preview-pane {
      position: sticky;
      top: ${PREVIEW_TOP_PX}px;
      align-self: start;
      max-height: calc(100vh - ${PREVIEW_TOP_PX + PREVIEW_BOTTOM_GAP_PX}px);
      overflow-y: auto;
      /* The corner sash hangs past the card's edge; clip it there rather
         than give the pane a sideways scrollbar. */
      overflow-x: hidden;
      border-radius: 8px;
    }
    /* Too narrow for two columns: the preview goes under the form, as an
       ordinary block -- a sticky box at the end of the page holds nothing. */
    @media (max-width: ${STACK_BELOW_PX}px) {
      .create-invoice-grid { grid-template-columns: minmax(0, 1fr); }
      .invoice-preview-pane { position: static; max-height: none; overflow: visible; }
    }
    /* Cmd+P: printSheetCss flattens the pane as an ancestor of the sheet;
       its screen-only height cap and scroll box must not clip the print. */
    @media print {
      .invoice-preview-pane {
        position: static !important;
        max-height: none !important;
        overflow: visible !important;
      }
    }
  `}
      </style>
      <div style={{ padding: 24 }}>
        <h2 style={{ marginTop: 0 }}>Create Invoice</h2>

        {err && (
          <div
            style={{
              background: "#fee2e2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              padding: 10,
              borderRadius: 8,
              marginBottom: 12,
            }}
          >
            {err}
          </div>
        )}

        <div className="create-invoice-grid">
          {/* Form */}
          <form
            onSubmit={saveInvoice}
            style={{
              background: isLocked ? "#f9fafb" : "white",
              padding: 12,
              border: isLocked ? "1px solid #fbbf24" : "1px solid #e5e7eb",
              borderRadius: 10,
              opacity: isLocked ? 0.85 : 1,
              position: "relative",
            }}
          >
            {/* Locked banner for non-draft invoices */}
            {isLocked && (
              <div style={{
                background: "#fef3c7",
                border: "1px solid #fbbf24",
                borderRadius: 6,
                padding: "8px 12px",
                marginBottom: 12,
                fontSize: 13,
                color: "#92400e",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}>
                <span>🔒</span>
                <span>This invoice is <strong>{invoiceStatus}</strong> and can only be edited by a manager.</span>
              </div>
            )}
            {prefill && isDraftForm && (
              <div
                style={{
                  background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 6,
                  padding: "8px 12px", marginBottom: 12, fontSize: 13, color: "#1E40AF",
                  display: "flex", alignItems: "center", gap: 8,
                }}
              >
                <span>
                  {/* "Prefilled from #" stays one literal: deploy-fe.sh checks
                      the bundle for it. */}
                  {(() => {
                    const src = prefill.number
                      ? `${displayInvoiceNumber(prefill.number)}${prefill.date ? ` (${prefill.date})` : ""}`
                      : null;
                    if (prefill.copied) {
                      return src ? `Copied from #${src}` : "Copied from an earlier invoice";
                    }
                    if (prefill.derived) {
                      return `Compared with the last invoice, #${src}`;
                    }
                    return src ? `Prefilled from #${src}` : "Prefilled from an earlier invoice";
                  })()}
                  {prefill.clientName ? `, ${prefill.clientName}` : ""}
                  {" "}— fields in{" "}
                  <span style={{ background: "#fef9c3", padding: "0 3px" }}>yellow</span>{" "}
                  are unchanged from it.
                </span>
                {/* Undo restores the form as it was before a prefill landed
                    in THIS session; a reopened draft has no such state. */}
                {prefillUndo && (
                <button
                  type="button"
                  onClick={() => {
                    if (prefillUndo) setForm(prefillUndo);
                    setPrefill(null);
                    setPrefillUndo(null);
                  }}
                  style={{
                    background: "none", border: "none", color: "#1D4ED8",
                    textDecoration: "underline", cursor: "pointer", font: "inherit",
                  }}
                >
                  Undo
                </button>
                )}
              </div>
            )}
            {/* Row 1: Client search + Bill To side by side */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              {/* Client search */}
              <div style={{ position: "relative" }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Client</label>
                <input
                  type="text"
                  placeholder="Select client…"
                  value={formClientQuery}
                  onChange={(e) => {
                    setFormClientQuery(e.target.value);
                    setFormClientId("");
                    setFormShowSuggestions(true);
                    setHighlightedClient(0);
                    setFormSaved(false);
                  }}
                  onKeyDown={(e) => {
                    const matches = matchingClients;
                    if (e.key === "Escape") {
                      setFormShowSuggestions(false);
                      return;
                    }
                    if (!formShowSuggestions || !matches.length) return;
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setHighlightedClient((i) => Math.min(i + 1, matches.length - 1));
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setHighlightedClient((i) => Math.max(i - 1, 0));
                    } else if (e.key === "Enter") {
                      // Without this the form submits and no client is picked.
                      e.preventDefault();
                      const picked = matches[highlightedClient] || matches[0];
                      if (picked) selectClient(picked);
                    }
                  }}
                  style={field}
                  required
                />
                {formShowSuggestions && formClientQuery && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: 6,
                      maxHeight: 220,
                      overflowY: "auto",
                      zIndex: 1000,
                    }}
                  >
                    {matchingClients
                      .map((c, i) => (
                        <div
                          key={c.id}
                          ref={(el) => {
                            clientRowsRef.current[i] = el;
                          }}
                          style={{
                            padding: "8px 10px",
                            cursor: "pointer",
                            background: i === highlightedClient ? "#eef2ff" : "white",
                          }}
                          onMouseEnter={() => setHighlightedClient(i)}
                          onClick={() => selectClient(c)}
                        >
                          {c.name}
                        </div>
                      ))}
                    {matchingClients.length === 0 && (
                      <div
                        style={{ padding: "8px 10px", cursor: "pointer", color: "#1D4ED8" }}
                        onClick={() => {
                          setAddingClient(true);
                          setFormShowSuggestions(false);
                        }}
                      >
                        + Add new customer
                      </div>
                    )}
                  </div>
                )}
                {addingClient && (
                  <AddClientInline
                    initialName={formClientQuery}
                    existingClients={clients}
                    onCancel={() => setAddingClient(false)}
                    onCreated={(client) => {
                      setAddingClient(false);
                      // AddClientInline's "Select it instead" hands back an
                      // EXISTING client (a name collision the user resolved),
                      // not a new one -- distinguish by whether its id is
                      // already in our list. An existing client has real
                      // invoice history, so it must behave exactly as if it
                      // had been picked from the suggestion list: same door,
                      // same behavior.
                      if (clients.some((c) => c.id === client.id)) {
                        selectClient(client);
                        return;
                      }
                      // Genuinely new: no invoice history to prefill from --
                      // only a group neighbour's invoice, which is a
                      // different client's bill. Deliberately NOT
                      // selectClient() for that reason.
                      setClients((prev) => [...prev, client]);
                      // This path deliberately bypasses selectClient, so it
                      // must do what selectClient does for the staleness
                      // guard: the ref is what tells an in-flight prefill for
                      // the PREVIOUS client that the selection has moved on.
                      // Without this, that prefill compares the old client
                      // against itself, matches, and lands a group sibling's
                      // lines and amounts on a brand-new client's invoice.
                      selectedClientIdRef.current = client.id;
                      setFormClientId(client.id);
                      setFormClientQuery(client.name);
                      setFormSaved(false);
                      setSelectedClientDetail(client);
                      setForm((prev) => ({
                        ...prev,
                        bill_to: client.bill_to || client.name || "",
                        care_of: "",
                      }));
                      // A brand-new client genuinely owes nothing, and has no
                      // "include previous balance" to inherit -- zero/off is
                      // the honest value, not whatever a previously selected
                      // client left behind.
                      setArBalance(0);
                      setArLoading(false);
                      setIncludePrevBalance(false);
                    }}
                  />
                )}
              </div>

              {/* Bill To */}
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Bill To</label>
                <input
                  type="text"
                  value={form.bill_to}
                  onChange={(e) => {
                    setForm({ ...form, bill_to: e.target.value });
                    setFormSaved(false);
                  }}
                  placeholder="Bill to name"
                  style={field}
                />
                {form.bill_to && selectedClient && form.bill_to !== (selectedClient.bill_to || selectedClient.name) && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await apiPatch(`/api/clients/${selectedClient.id}/`, { bill_to: form.bill_to });
                        // update local clients list so it reflects immediately
                        setClients((prev) => prev.map((c) => c.id === selectedClient.id ? { ...c, bill_to: form.bill_to } : c));
                      } catch (e) {
                        alert("Failed to save Bill To to client: " + e.message);
                      }
                    }}
                    style={{
                      marginTop: 4,
                      fontSize: 11,
                      padding: "2px 8px",
                      borderRadius: 4,
                      border: "1px solid #10b981",
                      background: "#f0fdf4",
                      color: "#065f46",
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    ↑ Save to client record
                  </button>
                )}
              </div>
            </div>

            {/* What is left to bill, straight under the client picker so it is
                in view before the fee is typed. */}
            {formClientId ? (
              <div style={{ marginBottom: 12 }}>
                <WipPanel
                  clientId={formClientId}
                  groupId={(selectedClientDetail && selectedClientDetail.group) || null}
                  excludeInvoiceId={editId || null}
                />
              </div>
            ) : null}
            {/* c/o -- a typed field, not just a checkbox.
                It used to be a checkbox bound to the client's contact and
                nothing else: no way to type one, no way to correct one, and no
                checkbox at all for a client with no contact on file. The
                invoice prints exactly one "c/o" whatever is typed here. */}
            <div style={{ marginBottom: 12 }}>
              <label
                htmlFor="invoice-care-of"
                style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}
              >
                c/o
              </label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  id="invoice-care-of"
                  type="text"
                  value={form.care_of}
                  placeholder="Leave blank for no c/o line"
                  onChange={(e) => {
                    setForm({ ...form, care_of: e.target.value });
                    setFormSaved(false);
                  }}
                  style={field}
                />
                {selectedClient?.contact &&
                  careOfLine(selectedClient.contact) !== careOfLine(form.care_of) && (
                    <button
                      type="button"
                      onClick={() => {
                        setForm({ ...form, care_of: selectedClient.contact });
                        setFormSaved(false);
                      }}
                      style={{ ...SMALL_LINK_BUTTON }}
                      title="Use the contact on the client record"
                    >
                      Use {selectedClient.contact}
                    </button>
                  )}
              </div>
            </div>

            {/* Who this invoice syncs to in QuickBooks. Above the address, so
                everything about WHO the invoice is for sits together -- and
                before the point where a sync to QuickBooks would be refused. */}
            <QboCustomerPanel
              clientId={formClientId}
              clientName={selectedClientDetail?.name || formClientQuery}
            />

            {/* The client's printed address. Saved to the CLIENT, not the
                invoice, so one entry fixes every invoice that client gets. */}
            {formClientId && (
              <div
                style={{
                  marginBottom: 12,
                  padding: "10px 12px",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  background: addressIsEmpty(addressForm) ? "#fffbeb" : "#f9fafb",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <strong style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", color: "#6b7280" }}>
                    Client address
                  </strong>
                  {addressIsEmpty(addressForm) && (
                    <span style={{ fontSize: 12, color: "#b45309" }}>
                      None on file — this invoice will print without one.
                    </span>
                  )}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 80px 1fr", gap: 8 }}>
                  {[
                    { key: "street_address", label: "Street address" },
                    { key: "city", label: "City" },
                    { key: "province", label: "Province" },
                    { key: "postal_code", label: "Postal code" },
                  ].map((f) => (
                    <div key={f.key}>
                      <label
                        htmlFor={`client-address-${f.key}`}
                        style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3 }}
                      >
                        {f.label}
                      </label>
                      <input
                        id={`client-address-${f.key}`}
                        type="text"
                        value={addressForm[f.key]}
                        onChange={(e) => {
                          setAddressForm({ ...addressForm, [f.key]: e.target.value });
                          setAddressSaved(false);
                        }}
                        style={field}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                  <button
                    type="button"
                    onClick={saveAddress}
                    disabled={addressSaving}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 6,
                      border: "1px solid #10b981",
                      background: "#f0fdf4",
                      color: "#065f46",
                      cursor: addressSaving ? "wait" : "pointer",
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  >
                    {addressSaving ? "Saving…" : "Save address"}
                  </button>
                  {addressSaved && (
                    <span style={{ fontSize: 12, color: "#065f46" }}>
                      Saved to the client record.
                    </span>
                  )}
                  {addressError && (
                    <span style={{ fontSize: 12, color: "#b91c1c" }}>{addressError}</span>
                  )}
                </div>
              </div>
            )}

            {/* Row 2: Date, Due Date, Number, Year End */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
              <div>
                <label htmlFor="invoice-date" style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Date</label>
                <input
                  id="invoice-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => {
                    setForm({ ...form, date: e.target.value });
                    setFormSaved(false);
                  }}
                  style={field}
                  required
                />
              </div>
              <div>
                <label htmlFor="invoice-due-date" style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Due Date</label>
                <input
                  id="invoice-due-date"
                  type="date"
                  value={form.due_date}
                  onChange={(e) => {
                    setForm({ ...form, due_date: e.target.value });
                    setFormSaved(false);
                  }}
                  style={field}
                />
              </div>
              <div>
                <label htmlFor="invoice-number" style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Invoice #</label>
                <input
                  id="invoice-number"
                  placeholder={
                    isDraftNumber(form.number)
                      ? `${displayInvoiceNumber(form.number)} · number assigned when finalised`
                      : editId ? "Number" : "Assigned when finalised"
                  }
                  // A draft's placeholder ("DRAFT-12") is not a number to edit:
                  // shown as the hint above, and sent back untouched on save.
                  value={isDraftNumber(form.number) ? "" : form.number}
                  readOnly={isDraftNumber(form.number) || (!editId && !numberUnlocked)}
                  title={
                    editId || numberUnlocked
                      ? "Edit the number by hand if you need to"
                      : "ClientZone assigns the number when you save"
                  }
                  onChange={(e) => {
                    setForm({ ...form, number: e.target.value });
                    setFormSaved(false);
                  }}
                  style={(() => {
                    const editable = !isDraftNumber(form.number) && (editId || numberUnlocked);
                    return {
                      ...field,
                      background: editable ? "white" : "#f3f4f6",
                      color: editable ? "inherit" : "#6b7280",
                      cursor: editable ? "text" : "not-allowed",
                    };
                  })()}
                />
                {!editId && (
                  <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>
                    {numberUnlocked ? (
                      <button
                        type="button"
                        onClick={() => {
                          setForm((prev) => ({ ...prev, number: "" }));
                          setNumberUnlocked(false);
                          setNumberClash(null);
                          setNumberHint(null);
                        }}
                        style={{ background: "none", border: "none", padding: 0, color: "#007E33", cursor: "pointer", fontSize: 10, textDecoration: "underline" }}
                      >
                        Number it when finalised instead
                      </button>
                    ) : (
                      <>
                        Assigned when finalised ·{" "}
                        <button
                          type="button"
                          onClick={() => setNumberUnlocked(true)}
                          style={{ background: "none", border: "none", padding: 0, color: "#007E33", cursor: "pointer", fontSize: 10, textDecoration: "underline" }}
                        >
                          Edit
                        </button>
                      </>
                    )}
                  </div>
                )}
                {!editId && numberUnlocked && !numberClash && numberHint && (
                  <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>
                    {numberHint.nextAfter === null ? (
                      <>Saved as <strong>{form.number}</strong> — the counter stays at {autoNumber}.</>
                    ) : numberHint.nextAfter === nextContiguous(form.number) ? (
                      <>Saved as <strong>{padNumber(form.number)}</strong>. Next invoice will be {numberHint.nextAfter}.</>
                    ) : (
                      <>Saved as <strong>{padNumber(form.number)}</strong>. Next invoice will be <strong>{numberHint.nextAfter}</strong>, not {nextContiguous(form.number)} — those numbers are in use.</>
                    )}
                  </div>
                )}
                {form.qbo_doc_number ? (
                  <div style={{ fontSize: 13, color: "#374151", marginTop: 4 }}>
                    QBO #: <strong>{form.qbo_doc_number}</strong>
                  </div>
                ) : null}
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>Year End</label>
                <select
                  value={form.year_end}
                  onChange={(e) => {
                    setForm({ ...form, year_end: parseInt(e.target.value) });
                    setFormSaved(false);
                  }}
                  style={field}
                >
                  <option value="">Select</option>
                  {Array.from({ length: 20 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {/* The sentence printed above the service bullets. Stored on the
                invoice and typed here: it used to be derived from Year End,
                which is the year time is booked against, not necessarily the
                year the client should read. Blank prints nothing. */}
            <div style={{ marginBottom: 12 }}>
              <label
                htmlFor="invoice-intro-line"
                style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}
              >
                Intro line
              </label>
              <input
                id="invoice-intro-line"
                type="text"
                value={form.intro_line}
                placeholder="Leave blank to print no intro line"
                onChange={(e) => {
                  setForm({ ...form, intro_line: e.target.value });
                  setFormSaved(false);
                }}
                style={field}
              />
            </div>

            {/* Lines */}
            <div style={{ marginBottom: 12 }}>
              <h4>Invoice Lines</h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "20px 1fr 150px 100px",
                  gap: 8,
                  fontWeight: "bold",
                  marginBottom: 6,
                }}
              >
                <div />
                <div>Description</div>
                <div>Fee</div>
                <div>Action</div>
              </div>
              {form.lines.map((line, idx) => {
                // The blue rule marking where a dragged line will land: above
                // this line, or -- for the last line only -- below it.
                const dropHere =
                  dragLine === null || dropGap === null ? null
                    : dropGap === idx ? "above"
                    : dropGap === idx + 1 && idx === form.lines.length - 1 ? "below"
                    : null;
                return (
                <div
                  key={idx}
                  data-invoice-line=""
                  data-drop={dropHere || undefined}
                  onDragOver={(e) => {
                    if (dragLine === null) return;
                    e.preventDefault();
                    if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
                    const gap = gapAt(e, idx);
                    if (gap !== dropGap) setDropGap(gap);
                  }}
                  onDrop={(e) => {
                    if (dragLine === null) return;
                    e.preventDefault();
                    moveLineTo(dragLine, gapAt(e, idx));
                    endLineDrag();
                  }}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "20px 1fr 150px 100px",
                    gap: 8,
                    marginBottom: 6,
                    alignItems: "start",
                    opacity: dragLine === idx ? 0.45 : 1,
                    boxShadow:
                      dropHere === "above" ? "0 -3px 0 0 #0073ea"
                        : dropHere === "below" ? "0 3px 0 0 #0073ea"
                        : "none",
                  }}
                >
                  {/* The grip. Only it starts a drag -- making the whole row
                      draggable would steal text selection in the description
                      and fee. Alt+Up/Down moves the line from the keyboard. */}
                  <button
                    type="button"
                    draggable
                    aria-label={`Drag to reorder line ${idx + 1}`}
                    title="Drag to reorder (or Alt+Up / Alt+Down)"
                    onDragStart={(e) => {
                      setDragLine(idx);
                      if (e.dataTransfer) {
                        e.dataTransfer.effectAllowed = "move";
                        // Firefox will not start a drag without data.
                        e.dataTransfer.setData("text/plain", String(idx));
                        const rowEl = e.currentTarget.closest("[data-invoice-line]");
                        if (rowEl && e.dataTransfer.setDragImage) {
                          e.dataTransfer.setDragImage(rowEl, 10, 10);
                        }
                      }
                    }}
                    onDragEnd={endLineDrag}
                    onKeyDown={(e) => {
                      if (!e.altKey) return;
                      if (e.key === "ArrowUp" && idx > 0) {
                        e.preventDefault();
                        moveLineTo(idx, idx - 1);
                      } else if (e.key === "ArrowDown" && idx < form.lines.length - 1) {
                        e.preventDefault();
                        moveLineTo(idx, idx + 2);
                      }
                    }}
                    style={{
                      marginTop: 30, padding: 0, width: 20, height: 28,
                      border: "none", background: "transparent",
                      cursor: "grab", color: "#9ca3af", fontSize: 16,
                      lineHeight: 1, letterSpacing: -2,
                    }}
                  >
                    ⋮⋮
                  </button>
                  <div style={{ minWidth: 0 }}>
                  {/* The firm writes the same dozen descriptions on every
                      invoice, 90 words at a time. A native select rather than
                      a custom menu: it is keyboard-reachable, it closes
                      itself, and it costs no markup per line. It writes a
                      starting point -- the wording is then altered for the
                      client, which is what it is for. */}
                  <select
                    aria-label={`Standard description for line ${idx + 1}`}
                    // Shows WHICH standard description this line is using,
                    // derived from the line itself rather than remembered --
                    // so altering the wording lets go of the preset, and
                    // picking the same one again resets an edited line.
                    value={presetValueFor(line.description, presetContext, presetLists)}
                    onChange={(e) => {
                      const text = e.target.value;
                      if (!text) return;
                      updateLine(idx, "description", fillPreset(text, presetContext));
                    }}
                    style={{
                      ...field,
                      marginBottom: 4,
                      fontSize: 12,
                      padding: "3px 6px",
                      // Grey while it is a prompt; ordinary text once it is
                      // reporting the line's actual standard description.
                      color: presetValueFor(line.description, presetContext, presetLists)
                        ? "#111827"
                        : "#6b7280",
                    }}
                  >
                    <option value="">Standard description…</option>
                    {presetsFor(selectedClientDetail, presetLists).map((group) => (
                      <optgroup key={group.name} label={group.name}>
                        {group.presets.map((preset) => (
                          <option key={preset.id ?? preset.label} value={preset.text}>
                            {preset.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {/* A textarea, not an input: a long description has to wrap and
                      stay readable. It grows to fit, so nothing hides behind a
                      scrollbar either. */}
                  <textarea
                    ref={autoGrow}
                    placeholder="Description"
                    value={line.description}
                    onChange={(e) => {
                      autoGrow(e.target);
                      updateLine(idx, "description", e.target.value);
                    }}
                    rows={1}
                    {...carriedMark(line.description, line._carried?.description)}
                    style={{
                      ...carriedMark(line.description, line._carried?.description).style,
                      minHeight: 38,
                      overflow: "hidden",
                      resize: "vertical",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "break-word",
                      fontFamily: "inherit",
                      fontSize: "inherit",
                      lineHeight: 1.4,
                    }}
                  />
                  </div>
                  <input
                    {...moneyInput}
                    placeholder="Amount"
                    value={line.amount}
                    onChange={(e) => updateLine(idx, "amount", e.target.value)}
                    {...carriedMark(line.amount, line._carried?.amount)}
                    // Level with the LAST line of the description, as the fee
                    // sits on the printed invoice -- not with the preset
                    // picker at the top of the row.
                    style={{
                      ...carriedMark(line.amount, line._carried?.amount).style,
                      alignSelf: "end",
                    }}
                  />
                  <div style={{ paddingTop: 6, display: "flex", alignItems: "center", gap: 2 }}>
                    <button
                      type="button"
                      aria-label={`Insert line above line ${idx + 1}`}
                      title="Insert a line above this one"
                      onClick={() => insertLineAbove(idx)}
                      style={iconBtn}
                    >
                      +
                    </button>
                    {form.lines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        style={linkBtn}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
              <button type="button" onClick={addLine} style={secondaryBtn}>
                + Add Line
              </button>
            </div>

            {/* Totals */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <input
                {...moneyInput}
                placeholder="Discount"
                value={form.discount}
                onChange={(e) => {
                  setForm({ ...form, discount: e.target.value });
                  setFormSaved(false);
                }}
                {...carriedMark(form.discount, prefill?.carried?.discount)}
              />
              <input
                {...moneyInput}
                placeholder="Disbursements"
                value={form.disbursements}
                onChange={(e) => {
                  setForm({ ...form, disbursements: e.target.value });
                  setFormSaved(false);
                }}
                {...carriedMark(form.disbursements, prefill?.carried?.disbursements)}
              />

              {/* Two figures that print BELOW the total and are not part of it:
                  a deposit already taken, and the portion of the base that
                  bears no PST (an out-of-province client). */}
              <div>
                <label
                  htmlFor="invoice-deposits-received"
                  style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}
                >
                  Deposits received
                </label>
                <input
                  id="invoice-deposits-received"
                  {...moneyInput}
                  value={form.deposits_received}
                  onChange={(e) => {
                    setForm({ ...form, deposits_received: e.target.value });
                    setFormSaved(false);
                  }}
                  style={field}
                />
              </div>
              <div>
                <label
                  htmlFor="invoice-tax-mode"
                  style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}
                >
                  Tax
                </label>
              <select
                id="invoice-tax-mode"
                value={form.tax_mode_ui}
                onChange={(e) => {
                  setForm({ ...form, tax_mode_ui: e.target.value });
                  setFormSaved(false);
                }}
                {...carriedMark(form.tax_mode_ui, prefill?.carried?.tax_mode)}
              >
                <option value="GST">GST only</option>
                <option value="GST_PST">GST + PST</option>
                <option value="HST">HST 13%</option>
                <option value="NONE">No tax</option>
              </select>
              </div>

              {/* Ticked, not typed. An exemption is a fact about the client --
                  they are outside the province -- and it covers the whole
                  invoice, so the old "PST exempt portion" field only ever
                  asked someone to restate the base and get it wrong. HST is a
                  MODE, so its box and the dropdown above are two views of one
                  value and cannot disagree. */}
              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <label
                  {...carriedTick(Boolean(form.pst_exempt), prefill?.carried?.pst_exempt)}
                  style={{
                    ...tickLabel,
                    ...carriedTick(Boolean(form.pst_exempt), prefill?.carried?.pst_exempt).style,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(form.pst_exempt)}
                    onChange={(e) => {
                      setForm({ ...form, pst_exempt: e.target.checked });
                      setFormSaved(false);
                    }}
                  />
                  PST exempt
                </label>
                <label
                  {...carriedTick(Boolean(form.gst_exempt), prefill?.carried?.gst_exempt)}
                  style={{
                    ...tickLabel,
                    ...carriedTick(Boolean(form.gst_exempt), prefill?.carried?.gst_exempt).style,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(form.gst_exempt)}
                    onChange={(e) => {
                      setForm({ ...form, gst_exempt: e.target.checked });
                      setFormSaved(false);
                    }}
                  />
                  GST exempt
                </label>
                <label style={tickLabel}>
                  <input
                    type="checkbox"
                    checked={form.tax_mode_ui === "HST"}
                    onChange={(e) => {
                      setForm({
                        ...form,
                        tax_mode_ui: e.target.checked ? "HST" : "GST_PST",
                      });
                      setFormSaved(false);
                    }}
                  />
                  HST 13%
                </label>
              </div>

              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                {(form.tax_mode_ui === "GST" ||
                  form.tax_mode_ui === "GST_PST") && (
                  <span>GST: ${form.tax_gst}</span>
                )}
                {form.tax_mode_ui === "GST_PST" && (
                  <span>PST: ${form.tax_pst}</span>
                )}
                {form.tax_mode_ui === "HST" && (
                  <span>HST: ${form.tax_hst}</span>
                )}
              </div>
            </div>

            <div style={{ fontWeight: "bold", marginBottom: 12 }}>
              Total: ${form.total_amount}
            </div>

            <label style={{ display: "block", marginTop: 8, marginBottom: 12, fontSize: 13 }}>
              <input type="checkbox"
                checked={!!form.do_not_sync}
                onChange={(e) => setForm((f) => ({ ...f, do_not_sync: e.target.checked }))} />
              {" "}Do not sync to QuickBooks (ClientZone only)
            </label>

            {numberClash && (
              <div
                style={{
                  background: "#fef3c7",
                  border: "1px solid #fbbf24",
                  color: "#92400e",
                  borderRadius: 6,
                  padding: "8px 12px",
                  marginBottom: 10,
                  fontSize: 13,
                }}
              >
                ⚠️ Invoice number <strong>{numberClash.number}</strong> is already used
                by <strong>{numberClash.client_name}</strong> (invoice #{numberClash.id}
                {numberClash.status ? `, ${numberClash.status}` : ""}). Change the number
                before saving.
              </div>
            )}

            {/* Save on the left, print on the right. Printing used to sit at
                the top-left of the PREVIEW pane, nowhere near the button you
                press to save. */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <button disabled={busy || isLocked} type="submit" style={{
                ...primaryBtn,
                background: isLocked ? "#9ca3af" : primaryBtn.background,
                cursor: isLocked ? "not-allowed" : "pointer",
              }}>
                {/* A new invoice is saved as a draft -- issuing is its own
                    step -- so the button says so. */}
                {busy
                  ? "Saving…"
                  : !editId
                    ? "Create Draft"
                    : isDraftForm
                      ? "Save Draft"
                      : "Update Invoice"}
              </button>

              {showIssue && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={issueInvoice}
                  title="Give the invoice its number so it can be printed and sent. Does not send it to QuickBooks."
                  style={{ ...primaryBtn, background: "#007E33" }}
                >
                  Issue invoice
                </button>
              )}

              {showSync && (
                <button
                  type="button"
                  disabled={syncBusy}
                  onClick={syncToQbo}
                  aria-label={SYNC_LABEL}
                  title="Push this invoice, as last saved, to QuickBooks."
                  style={{ ...secondaryBtn, marginTop: 8, fontSize: 13 }}
                >
                  {syncBusy ? "Syncing…" : SYNC_LABEL}
                </button>
              )}

              <button
                id="print-button"
                type="button"
                onClick={handlePrintToPDF}
                style={{
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  background: "#f9fafb",
                  cursor: "pointer",
                  fontSize: 13,
                  fontFamily: "system-ui, sans-serif",
                }}
              >
                Print to PDF
              </button>
            </div>

            {(syncMessage || savedSyncProblem) && (
              <div
                role="status"
                style={{
                  marginTop: 10,
                  padding: "8px 12px",
                  borderRadius: 6,
                  fontSize: 13,
                  background: syncMessage?.ok ? "#f0fdf4" : "#fef2f2",
                  border: `1px solid ${syncMessage?.ok ? "#86efac" : "#fca5a5"}`,
                  color: syncMessage?.ok ? "#166534" : "#991b1b",
                }}
              >
                {syncMessage ? syncMessage.text : savedSyncProblem}
              </div>
            )}

            {/* A/R Balance section */}
            {formClientId && (
              <div style={{
                marginTop: 14,
                padding: "10px 12px",
                background: arBalance > 0 ? "#fef9ec" : "#f0fdf4",
                border: `1px solid ${arBalance > 0 ? "#fcd34d" : "#86efac"}`,
                borderRadius: 7,
                fontSize: 13,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: arBalance > 0 ? 8 : 0 }}>
                  <span style={{ fontWeight: 600, color: "#374151" }}>
                    Open A/R Balance
                  </span>
                  <span style={{
                    fontWeight: 700,
                    color: arBalance > 0 ? "#b45309" : "#15803d",
                    fontSize: 14,
                  }}>
                    {arLoading ? "…" : arBalance !== null
                      ? `$${arBalance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : "—"}
                  </span>
                </div>
                {arBalance > 0 && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", color: "#374151" }}>
                    <input
                      type="checkbox"
                      checked={includePrevBalance}
                      onChange={(e) => setIncludePrevBalance(e.target.checked)}
                      style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#b45309" }}
                    />
                    <span>Include previous balance on invoice</span>
                  </label>
                )}
              </div>
            )}

            {/* What this client was billed the last few times, beside the
                A/R balance. The unbilled work sits at the top of the form. */}
            {formClientId ? (
              <PreviousInvoicesPanel
                clientId={formClientId}
                excludeInvoiceId={editId || null}
              />
            ) : null}

          </form>

          {/* Preview, in a pane that keeps it in view while the form scrolls
              (see .invoice-preview-pane). The pane, not #invoice-preview, is
              what sticks: the sheet itself is what both print paths read. */}
          <div className="invoice-preview-pane" data-testid="invoice-preview-pane">
          <div
            id="invoice-preview"
            style={{
              position: "relative",
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: "50px 70px",
              // Exactly the pane's height, so a short invoice fills it with no
              // scrollbar. Screen-only: printSheetCss overrides both.
              boxSizing: "border-box",
              minHeight: `calc(100vh - ${PREVIEW_TOP_PX + PREVIEW_BOTTOM_GAP_PX}px)`,
              fontFamily: "'Times New Roman', Times, serif",
              fontSize: "13px",
              lineHeight: 1.5,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Printing moved to the form's action row, beside Save. */}
            <div
              style={{
                position: "absolute",
                top: 10,
                left: 10,
                display: "flex",
                gap: "8px",
                zIndex: 10,
              }}
            >
              <button
                id="edit-button"
                onClick={() => {
                  if (editId) {
                    window.location.href = `/invoices/create?id=${editId}`;
                  } else {
                    alert(
                      "This invoice hasn’t been saved yet. Save it first, then edit."
                    );
                  }
                }}
                style={{
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "1px solid #ef4444",
                  background: "#fff5f5",
                  color: "#b91c1c",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontFamily: "system-ui, sans-serif",
                  fontWeight: 600,
                }}
              >
                Edit Invoice
              </button>
            </div>

            {/* Pretty Sash */}
            <div
              id="preview-sash"
              style={{
                position: "absolute",
                top: 20,
                right: -50,
                background: "linear-gradient(135deg, #4f46e5, #111827)",
                color: "white",
                transform: "rotate(45deg)",
                padding: "6px 50px",
                fontWeight: 700,
                boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
                borderRadius: 4,
              }}
            >
              {formSaved ? "Draft" : "Preview"}
            </div>

            <InvoiceDocument
              fontSize={invoiceFontSize}
              invoice={{
                ...previewData,
                previous_balance:
                  includePrevBalance && arBalance > 0 ? arBalance : 0,
              }}
              client={clientForPreview}
            />
          </div>
          </div>
        </div>
      </div>

      <ExistingDraftChoice
        clientName={existingDrafts?.clientName}
        drafts={existingDrafts?.drafts}
        onCreateAnother={() => setExistingDrafts(null)}
        onEdit={(draft) => {
          setExistingDrafts(null);
          // The new invoice is being abandoned: nothing still in flight for
          // it (the prefill, the client detail, the A/R) may land on the
          // draft about to load.
          selectedClientIdRef.current = null;
          setPrefill(null);
          setPrefillUndo(null);
          navigate(`/invoices/create?id=${draft.id}`);
        }}
      />

      <PageBreakChoice
        open={Boolean(printChoice)}
        pagesIfFlowed={printChoice?.pages}
        onChoose={choosePageMode}
        onCancel={() => setPrintChoice(null)}
      />
    </>
  );
}